import { HateoasLink } from "components/types";
import { EnhancedAccount } from "library/accounts/types";
import { AddressesApiResponse } from "library/api/address/types.generated";
import {
  AccountWithPersonalDetails,
  MaskedPersonalDetails,
  ParsedAccountsAddressesResponse,
  ResidentialFormValues,
} from "./types";

export function isMailingAddressSameAsResidential(
  accountAddress: AccountWithPersonalDetails,
): boolean {
  if (accountAddress.personalDetails?.type !== "full") {
    return false;
  }

  const { mailing, residential } = accountAddress.personalDetails;

  if (mailing === null) {
    return true;
  }

  return (
    mailing?.address1 === residential.address1 &&
    mailing?.address2 === residential.address2 &&
    mailing?.city === residential.city &&
    mailing?.state === residential.state &&
    mailing?.post_code === residential.post_code &&
    mailing?.country === residential.country
  );
}

export function parseAddressesResponse(
  apiAddresses: AddressesApiResponse,
  enhanced: EnhancedAccount[],
): ParsedAccountsAddressesResponse {
  let otpNeeded = false;
  const accountsWithPersonalDetails: AccountWithPersonalDetails[] =
    enhanced.map((account) => {
      if (account.type === "PERSONAL_LOAN") {
        return { account, status: "PERSONAL_LOAN" };
      }

      if (account.joint_account === true) {
        return { account, status: "JOINT_ACCOUNT" };
      }

      if (!account.credit_card?.token) {
        return { account, status: "SOMETHING_WENT_WRONG" };
      }

      const personalDetails = apiAddresses.accounts.find(
        ({ token }) => token === account.credit_card?.token,
      );

      if (!personalDetails) {
        return { account, status: "SOMETHING_WENT_WRONG" };
      }

      if ("error" in personalDetails) {
        return { account, status: "SOMETHING_WENT_WRONG" };
      }

      if (
        personalDetails.type === "full" &&
        !personalDetails.mobile_phone &&
        !personalDetails.home_phone &&
        !personalDetails.work_phone
      ) {
        return { account, status: "SOMETHING_WENT_WRONG" };
      }

      if (personalDetails.type === "full" && !personalDetails.residential) {
        return { account, status: "SOMETHING_WENT_WRONG" };
      }

      if (personalDetails.type === "full" && !personalDetails.residential) {
        return { account, status: "SOMETHING_WENT_WRONG" };
      }

      if (
        personalDetails.type === "masked" &&
        personalDetails.restricted === false
      ) {
        otpNeeded = true;
      }

      let updateEndpoint = "";
      let updateMailingEndpoint = "";
      let updateEmailEndpoint = "";
      let updatePhoneEndpoint = "";

      if (personalDetails?.type === "full" && !!personalDetails.links) {
        const updateLink = personalDetails.links.find(
          (link) => link.rel === "update_residential_address",
        )?.href;

        if (!updateLink) {
          console.error("Update residential address url wasn't supplied");
          throw new window.Error("Invalid response");
        }
        updateEndpoint = updateLink;

        const updateMailingLink = personalDetails.links.find(
          (link) => link.rel === "update_mailing_address",
        )?.href;

        if (!updateMailingLink) {
          console.error("Update mailing address url wasn't supplied");
          throw new window.Error("Invalid response");
        }
        updateMailingEndpoint = updateMailingLink;

        const updateEmailLink = personalDetails.links.find(
          (link) => link.rel === "update_email",
        )?.href;

        if (!updateEmailLink) {
          console.error("Update mailing address url wasn't supplied");
          throw new window.Error("Invalid response");
        }

        updateEmailEndpoint = updateEmailLink;
        const updatePhoneLink = personalDetails.links.find(
          (link) => link.rel === "update_phone_details",
        )?.href;

        if (!updatePhoneLink) {
          console.error("Update phone details url wasn't supplied");
          throw new window.Error("Invalid response");
        }
        updatePhoneEndpoint = updatePhoneLink;
      }

      const enhancedPersonalDetails =
        personalDetails.type === "full" && personalDetails.residential
          ? {
              token: personalDetails.token,
              emailAddress: personalDetails.email,
              mobilePhone: personalDetails.mobile_phone,
              homePhone: personalDetails.home_phone,
              workPhone: personalDetails.work_phone,
              type: personalDetails.type,
              residential: personalDetails.residential!,
              mailing: personalDetails.mailing,
              error: personalDetails.error,
              links: personalDetails.links,
              updateEndpoint,
              updateMailingEndpoint,
              updateEmailEndpoint,
              updatePhoneEndpoint,
            }
          : ({
              ...personalDetails,
              emailAddress: personalDetails.email,
              homePhone: personalDetails.home_phone,
              mobilePhone: personalDetails.mobile_phone,
              workPhone: personalDetails.work_phone,
            } as MaskedPersonalDetails);

      return {
        account,
        personalDetails: {
          ...enhancedPersonalDetails,
          canEdit: "links" in personalDetails,
        },
        status: "SUCCESS",
      };
    });

  if (!otpNeeded) {
    return { otpNeeded, accountsWithPersonalDetails };
  }

  const createSessionUrl = getCreateSessionUrl({ links: apiAddresses.links });

  if (createSessionUrl === "") {
    console.error("create session url wasn't supplied");
    throw new window.Error("Invalid response");
  }

  return { otpNeeded, accountsWithPersonalDetails, createSessionUrl };
}

export const residentialFormValuesToAddress = (
  address: ResidentialFormValues["address"],
) => {
  if (!("state" in address)) {
    return {
      address1: address.address1,
      address2: address.address2 ?? "",
      city: address.city,
      suburb: address.nzSuburb ?? "",
      post_code: address.postcode,
      country: "New Zealand",
    };
  }

  return {
    address1: address.address1,
    address2: address.address2 ?? "",
    city: address.city,
    state: address.state ?? "",
    post_code: address.postcode,
    country: "Australia",
  };
};

interface Param {
  links?: HateoasLink[];
}

export const getCreateSessionUrl = ({ links = [] }: Param) =>
  links.find((link) => link.rel === "create_otp_session")?.href ?? "";

export function getCountryCodePrefix(country?: string): string {
  switch (country) {
    case "AUSTRALIA": {
      return "+61";
    }

    case "NEW ZEALAND": {
      return "+64";
    }

    default: {
      return "";
    }
  }
}
